<template>
  <div :ref="'component' + claimId" class="card my-4 mx-2">

    <!-- Header -->
    <div class="card-header">
      <div class="row">
        <div class="col-md-4">

          <span class="text-nowrap">
            <a @click.prevent="updateStatus"
               class="btn btn-xs btn-default btn-spin-on-click mr-2"
               data-toggle="tooltip" title="Update status">
              <i class="fa fa-refresh" :class="{'fa-spin': statusIsUpdating}"></i>
            </a>
            <a :href="claimLink"><i class="fa fa-newspaper-o"></i> {{ claimNumber }}</a>
          </span>

          <template v-if="!claimClosed">
            <span class="label label-important ml-1">Days open: {{ claimDaysOpen }}</span>
            <span class="ml-1" v-html="claimStatus"></span>
          </template>
          <span v-else class="label label-danger ml-1">Closed</span>

        </div>
        <div class="col-md-4 text-md-center">

          <div v-if="claimOnHold" class="mb-1">
            <span class="label label-warning"><i class="fa fa-clock-o"></i> On hold</span>
          </div>

          <div v-if="claimReopened" class="mb-1">
            Reopen: {{ claimReopenDate }}
            <span v-if="!claimClosed" class="label label-important">Days reopen: {{ claimDaysReopen }}</span>
          </div>

          <div v-if="hasFieldClaim">
            <a :href="fieldClaimLink"><i class="fa fa-newspaper-o"></i> Task/Field</a>
            <template v-if="!fieldClaimClosed">
              <span class="label label-important ml-1">Days open: {{ fieldClaimDaysOpen }}</span>
              <span class="ml-1" v-html="fieldClaimStatus"></span>
            </template>
            <span v-else class="label label-danger ml-1">Closed</span>
          </div>

        </div>
        <div class="col-md-4 text-md-right">
          {{ client }}
        </div>
      </div>
    </div>
    <!-- /Header -->

    <!-- Body -->
    <div class="card-body p-1" :class="{'bg-grey': blockEditing}">
      <div v-if="statusPending" class="alert alert-warning text-md-center my-2">
        <small>Compliance status pending. Please <a @click.prevent="updateStatus" href="#"><i class="fa fa-refresh" :class="{'fa-spin': statusIsUpdating}"></i> click here</a> to update or check back later...</small>
      </div>
      <template v-if="!statusPending && claimTabViewMode">
        <div v-if="claimOnHold" class="alert alert-warning text-md-center my-2">
          <i class="fa fa-clock-o"></i> Currently, the claim is on hold.
        </div>
        <template v-else>
          <div v-if="status" class="alert alert-success text-md-center my-2">
            <i class="fa fa-check"></i> Currently, the claim is compliant with all Citizens' requirements.
          </div>
          <div v-else class="alert alert-danger text-md-center my-2">
            <i class="fa fa-remove"></i> Currently, the claim is out of compliance.
          </div>
        </template>
      </template>

      <!-- View mode -->
      <div v-if="mode === 'view'">
        <template v-for="(group, groupIndex) in fieldsByGroups">
          <div v-if="group.length" class="row">
            <div v-if="groupIndex > 0" class="col-md-12"><hr class="my-2"></div>
            <div v-for="fieldGroup in group" class="col-md-4">
              <ul class="list-group clear-list mt-0">

                <template v-for="fieldData in fieldGroup">
                  <li v-if="fieldData.allowed" class="list-group-item fist-item py-1">
                  <span class="float-right d-inline-block text-truncate" style="max-width: 200px;">
                    <a href @click.prevent="showFieldForm(fieldData)">
                      {{ fieldData.value }}
                    </a>
                  </span>
                    <span class="d-inline-block text-truncate" style="max-width: 170px;">
                    <span v-if="!claimOnHold" :class="fieldData.statusLabelClass" class="p-0 mr-1" v-tooltip :title="fieldData.statusDescription">
                      <span v-html="fieldData.statusIcon"></span>
                    </span>
                    <span v-tooltip :title="fieldData.title">
                      {{ fieldData.title }}
                    </span>
                  </span>
                  </li>
                </template>

              </ul>
            </div>
          </div>
        </template>
      </div>

      <div v-if="!statusPending && showOnlyCompliantFields && !fieldsByViewMode.length" class="text-center alert alert-success mb-0">
        <i class="fa fa-check"></i> There are currently no non-compliant fields in this claim.
      </div>

      <!-- Fast edit (single field form) -->
      <modal :id="modalId" :title="field.title" @shown="formShown">
        <form :id="fieldFormId" method="post" :action="fieldFormAction" @submit.prevent="onFieldFormSubmit">

          <div class="text-center mb-4">
            <a :href="claimLink"><i class="fa fa-newspaper-o"></i> {{ client }} - {{ claimNumber }}</a>
          </div>

          <div class="text-center mb-2">Current value:</div>
          <div class="row">
            <div class="col-md-6 offset-md-3 field-area text-center">
            <span v-if="!claimOnHold" :class="field.statusLabelClass" class="p-0 mr-1">
              <span v-html="field.statusIcon"></span>
            </span>
              <strong v-html="field.html"></strong>
            </div>
          </div>

          <template v-if="!(field.readonly || readonly)">
            <div class="text-center mt-4 mb-2">New value:</div>
            <div class="row">
              <div class="col-md-6 offset-md-3 field-area">
                <div v-if="!isComponent(field)" v-html="field.form"></div>
                <component v-if="isComponent(field)" :is="componentName(field)" v-bind="componentProps(field)"></component>
                <div v-if="fieldFormError" class="mt-3 mb-0 alert alert-danger text-center">
                  <i class="fa fa-exclamation-triangle"></i> {{ fieldFormError }}
                </div>
              </div>
            </div>

            <div class="row mt-4 mb-0">
              <div class="col-md-6 offset-md-3 field-area">
                <textarea v-model="note" class="form-control" placeholder="Enter a journal note here or leave blank to ignore."></textarea>
              </div>
            </div>
          </template>

        </form>

        <template v-slot:footer>
          <button type="button" class="btn btn-white" @click="close">Close</button>
          <button v-if="!(field.readonly || readonly)" type="submit" class="btn btn-primary" :form="fieldFormId" :disabled="saving">
            <i v-if="saving" class="fa fa-spinner fa-spin"></i> Save changes
          </button>
        </template>
      </modal>

      <!-- Edit mode -->
      <template v-if="mode === 'edit'">

        <form :id="formId" method="post" :action="formAction" @submit.prevent="onFormSubmit">
        <template v-for="(group, groupIndex) in editFieldsByGroups">

          <div v-if="group.length" class="row">
            <div v-if="groupIndex > 0" class="col-md-12"><hr class="my-2"></div>
            <div v-for="fieldGroup in group" class="col-md-4">

              <div v-for="fieldData in fieldGroup" class="my-2 pb-2"
                   :data-toggle="fieldDataToggleDependent(fieldData)"
                   :data-values="fieldDataToggleValue(fieldData)">
                <div>
                  <span :class="fieldData.statusLabelClass" class="p-0 mr-1" v-tooltip :title="fieldData.statusDescription">
                    <span v-html="fieldData.statusIcon"></span>
                  </span>
                  {{ fieldData.title }}:
                </div>
                <div>
                  <template v-if="!fieldData.readonly">
                    <div v-if="!isComponent(fieldData)" v-html="fieldData.form"></div>
                    <component v-if="isComponent(fieldData)" :is="componentName(fieldData)" v-bind="componentProps(fieldData)"></component>
                  </template>

                  <input v-else type="text" :value="fieldData.value" class="form-control form-control-sm" readonly disabled>

                  <template v-for="inputName in fieldData.formFields">
                    <div v-if="formErrors[inputName]" class="mt-1 mb-0 alert alert-danger text-center">
                      <i class="fa fa-exclamation-triangle"></i> {{ formErrors[inputName] }}
                    </div>
                  </template>
                </div>

              </div>

            </div>
          </div>


        </template>
        </form>

      </template>
    </div>
    <!-- /Body -->

    <!-- Footer -->
    <div v-if="!readonly" class="card-footer text-center">
      <template v-if="mode === 'view'">
        <div class="row">
          <div class="col-md-6 offset-md-3">
            <button @click.prevent="toEditMode" class="btn btn-primary" :disabled="blockEditing"><i class="fa fa-edit"></i> Edit</button>
          </div>

          <div class="col-md-3">
            <transition name="slide-fade">
              <div class="text-right" v-if="showSavedNotification">
                <span class="notification text-success"><i class="fa fa-fw fa-check-circle-o"></i> Saved</span>
              </div>
            </transition>
            <transition name="slide-fade">
              <div class="text-right" v-if="showClosedNotification">
                <span class="notification text-muted"><i class="fa fa-fw fa-ban"></i> Not saved</span>
              </div>
            </transition>
          </div>
        </div>
      </template>

      <template v-if="mode === 'edit'">
        <div class="row mb-3 mb-0">
          <div class="col-md-8 offset-md-2 field-area">
            <textarea v-model="note" name="note" class="form-control" placeholder="Enter a journal note here or leave blank to ignore." :form="formId"></textarea>
          </div>
        </div>

        <div v-if="formError" class="my-3 alert alert-danger text-center">
          <i class="fa fa-exclamation-triangle"></i> {{ formError }}
        </div>

        <button @click.prevent="toViewMode(true);" class="btn btn-default mx-1"><i class="fa fa-fw fa-ban"></i> Cancel</button>
        <button type="submit" class="btn btn-primary mx-1" :form="formId">
          <i v-if="saving" class="fa fa-spinner fa-spin"></i> Save changes
        </button>
      </template>
    </div>
    <!-- /Footer -->

  </div>
</template>

<script>
import HasModal from "../mixins/HasModal"
import cims from "../../cims"
import { mapState } from 'pinia'
import { mapActions } from 'pinia'
import { useCitizensTrackingStore } from "../../stores/citizensTracking"

export default {
  mixins: [HasModal],

  props: {
    initialData: {type: Object, required: true},
    groups: {type: Array, required: true},
    client: {type: String, required: true},
    claimId: {type: Number, required: true},
    claimStatusPending: {type: Boolean, required: true},
    claimLink: {type: String, required: true},
    claimNumber: {type: String, required: true},
    claimDaysOpen: {type: Number, required: true},
    claimStatus: {type: String, required: true},
    claimOnHold: {type: Boolean, default: false},
    claimClosed: {type: Boolean, required: true},
    claimReopened: {type: Boolean, required: true},
    claimReopenDate: {type: String, default: ''},
    claimDaysReopen: {type: Number, default: 0},
    hasFieldClaim: {type: Boolean, required: true},
    fieldClaimLink: {type: String, default: ''},
    fieldClaimDaysOpen: {type: Number, default: 0},
    fieldClaimStatus: {type: String, default: ''},
    fieldClaimClosed: {type: Boolean, default: false},
    fieldFormAction: {type: String, required: true},
    formAction: {type: String, required: true},
    fieldsRoute: {type: String, required: true},
    statusRoute: {type: String, required: true},
    readonlyMode: {type: Boolean, default: true},
    claimTabViewMode: {type: Boolean, default: false},
  },

  data() {
    return {
      fields: [],
      status: false,
      field: {'name': '', 'title': '', 'value': '', 'html': '', status: 1, readonly: true, form: '', allowed: false, formFields: []},
      mode: 'view', // One of "view", "edit".
      blockEditing: false,
      fieldFormError: '',
      formErrors: {},
      formError: '',
      saving: false,
      note: '',
      statusIsUpdating: false,
      statusPending: this.claimStatusPending,
      showSavedNotification: false,
      showClosedNotification: false,
      readonly: this.readonlyMode || this.claimOnHold,
    }
  },

  methods: {

    toEditMode() {
      this.mode = 'edit';
      this.resetJournalOptions();
      this.updateEditModeClaim(this.claimId);
      this.$nextTick(() => {
        this.formShown();
      });
    },

    toViewMode(showClosedNotification = false) {
      this.mode = 'view';
      this.resetJournalOptions();
      this.editModeClaimUpdated();
      this.scrollToTop();
      if (showClosedNotification) {
        this.showClosedNotification = true;
        this.$nextTick(() => {
          this.showClosedNotification = false;
        });
      }
    },

    showFieldForm(fieldData) {
      this.field = fieldData;
      this.showModal();
    },

    onFormSubmit(e) {
      const formData = new FormData(e.target);
      formData.append('_method', 'PATCH');
      this.submitForm(formData);
    },

    submitForm(formData) {
      this.saving = true;
      this.formError = '';
      this.formErrors = {};
      axios.post(this.formAction, formData)
        .then(response => {
          this.updateStatus();
          this.toViewMode();
          this.showSavedNotification = true;
        })
        .catch(error => {
          this.handleErrors(error, 'Unable to process request.')
        })
        .then(() => {
          this.saving = false;
        });
    },

    onFieldFormSubmit(e) {
      const formData = new FormData(e.target);
      formData.append('_method', 'PATCH');
      formData.append('field_name', this.field.name);
      if (this.note.trim() !== '') formData.append('note', this.note);
      this.submitFieldForm(formData);
    },

    submitFieldForm(formData) {
      this.saving = true;
      this.fieldFormError = '';
      axios.post(this.fieldFormAction, formData)
        .then(response => {
          this.field.value = response.data.value;
          this.field.html = response.data.html;
          this.field.status = response.data.status;
          this.field.statusLabelClass = response.data.statusLabelClass;
          this.field.statusIcon = response.data.statusIcon;
          this.field.statusDescription = response.data.statusDescription;
          this.field.form = response.data.form;

          let updatedFields = this.fields;
          let index = this.fields.findIndex(f => f.name === this.field.name);
          updatedFields[index] = this.field;
          this.fields = updatedFields;

          this.updateStatus();
          this.hideModal();
        })
        .catch(error => {
          this.handleFieldErrors(error, 'Unable to process request.')
        })
        .then(() => {
          this.saving = false;
        });
    },

    reloadFields() {
      axios.get(this.fieldsRoute)
        .then(response => {
          this.fields = response.data.fields;
          this.status = response.data.status;
          this.showSavedNotification = false;
        });
    },

    updateStatus() {
      this.statusIsUpdating = true;
      axios.get(this.statusRoute)
        .then(response => {
          this.statusPending = false;
          this.statusIsUpdating = false;
          this.reloadFields();
        });
    },

    handleErrors(errorObj, defaultMessage) {
      if (errorObj.response.status === 422) {
        let errors = errorObj.response.data.errors || {};
        Object.keys(errors).forEach(field => {
          this.formErrors[field] = errors[field].join(' ');
        })
        this.formError = 'One or more fields have an error. Please check and try again.';
      } else {
        if (errorObj.response.status === 403) {
          this.formError = defaultMessage + ' Access denied.';
        } else {
          this.formError = defaultMessage;
        }
      }
    },

    handleFieldErrors(errorObj, defaultMessage) {
      if (errorObj.response.status === 422) {
        let errors = errorObj.response.data.errors || {};
        let errorMessages = [];
        Object.keys(errors).forEach(field => {
          errorMessages.push(errors[field].join(' '));
        })
        this.fieldFormError = errorMessages.join(' ');
      } else {
        if (errorObj.response.status === 403) {
          this.fieldFormError = defaultMessage + ' Access denied.';
        } else {
          this.fieldFormError = defaultMessage;
        }
      }
    },

    close() {
      this.fieldFormError = '';
      this.hideModal();
    },

    formShown() {
      cims.addDataDependentFields();
      cims.addDatePickers();
      cims.addDatePickersNoCurrent();
      cims.addDateTimePickers();
      cims.addDateTimePickersNoCurrent();

      this.resetJournalOptions();
    },

    resetJournalOptions() {
      this.note = '';
    },

    fieldDataToggleDependent(field) {
      if (typeof field.visibleIf === 'boolean') return null;
      return field.visibleIf.length ? 'dependent' : null;
    },

    fieldDataToggleValue(field) {
      if (typeof field.visibleIf === 'boolean') return null;
      if (!field.visibleIf.length) return null;
      return JSON.stringify(field.visibleIf);
    },

    scrollToTop() {
      const element = this.$refs['component' + this.claimId];
      element.scrollIntoView({ behavior: "smooth" });
    },

    isComponent(field) {
      return field.form.slice(0, 14) === 'vue.component:';
    },

    componentName(field) {
      if (!this.isComponent(field)) return '';
      const componentData = JSON.parse(field.form.substring(14));
      return componentData.name;
    },

    componentProps(field) {
      if (!this.isComponent(field)) return {};
      const componentData = JSON.parse(field.form.substring(14));
      return componentData.props;
    },

    ...mapActions(useCitizensTrackingStore, ['updateEditModeClaim', 'editModeClaimUpdated']),
  },

  computed: {

    fieldsByViewMode() {
      let fields = this.fields;
      if (this.showOnlyCompliantFields) {
        fields = fields.filter(f => (f.status === 0) || (f.status === 2));
      }
      return fields;
    },

    fieldsByGroups() {
      let fieldsByGroups = [];
      this.groups.forEach((group, index) => {
        fieldsByGroups[index] = this.fieldsByViewMode.filter(f => f.group === index);
        fieldsByGroups[index] = fieldsByGroups[index].filter(f => f.allowed);
        fieldsByGroups[index] = this.splitArrayToGroups(fieldsByGroups[index], 3);
      });
      return fieldsByGroups;
    },

    editFieldsByGroups() {
      let fieldsByGroups = [];
      this.groups.forEach((group, index) => {
        fieldsByGroups[index] = this.fields.filter(f => f.group === index);
        fieldsByGroups[index] = fieldsByGroups[index].filter(f => f.visibleIf !== false);
        fieldsByGroups[index] = this.splitArrayToGroups(fieldsByGroups[index], 3);
      });
      return fieldsByGroups;
    },

    formId() {
      return 'citizens-form-' + this.claimId;
    },

    fieldFormId() {
      return 'citizens-form-' + this.field.name + '-' + this.claimId;
    },

    ddChangeReasons() {
      let reasons = this.ddReasons;
      reasons[-1] = 'Other';
      return reasons;
    },

    showCustomDDReasonField() {
      return (this.ddReasonId === -1) || (this.ddReasonId === '-1');
    },

    ...mapState(useCitizensTrackingStore, ['showOnlyCompliantFields', 'editModeClaimId'])
  },

  mounted() {
    this.status = this.initialData.status;
    this.fields = this.initialData.fields;
  },

  watch: {
    editModeClaimId(value) {
      this.blockEditing = false;
      if (value === 0) return;
      if (value !== this.claimId) {
        this.blockEditing = true;
      }
    },
  },
}
</script>

<style scoped>
.field-area {
  border: none;
  border-radius: 10px;
  padding: 14px;
  box-shadow: rgba(50, 50, 93, 0.25) 0 2px 5px -1px, rgba(0, 0, 0, 0.3) 0 1px 3px -1px;
}

.slide-fade-enter-active {
  transition: all 1s ease-out;
}
.slide-fade-leave-active {
  transition: all 3s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.notification {
  font-size: 1.7em;
}
</style>
